import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

export type TargetMode = 'existing' | 'new-video';

interface Props {
  mode: TargetMode;
  onChange: (next: TargetMode) => void;
}

/**
 * Tab strip at the top of the YouTube target step: pick an existing
 * video, or upload the session's source media as a new one. Shown only
 * for YouTube connections — iBabs has nothing analogous.
 */
export default function StepChooseTargetModeTabs({ mode, onChange }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const tabStyle = (active: boolean) => ({
    background: active ? getColour('secondLayer') : 'transparent',
    color: active ? getColour('activeText') : getColour('inactiveText')
  });

  return (
    <div
      role="tablist"
      aria-label={t('publish-choose-target-mode')}
      className="flex items-center gap-1 rounded-lg border p-1"
      style={{
        borderColor: getColour('hairline'),
        background: getColour('firstLayer')
      }}
    >
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'existing'}
        onClick={() => onChange('existing')}
        className="flex-1 rounded-md px-3 py-1.5 text-xs font-medium outline-none focus-visible:ring-2"
        style={tabStyle(mode === 'existing')}
      >
        {t('publish-target-mode-existing')}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'new-video'}
        onClick={() => onChange('new-video')}
        className="flex-1 rounded-md px-3 py-1.5 text-xs font-medium outline-none focus-visible:ring-2 inline-flex items-center justify-center gap-1.5"
        style={tabStyle(mode === 'new-video')}
      >
        <Upload className="h-3.5 w-3.5" aria-hidden="true" />
        {t('publish-target-mode-new-video')}
      </button>
    </div>
  );
}
